"use client"
import { useRouter } from "next/navigation"
import Link from "next/link"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/fun-ui/Popover";
import { UserAvatar } from "@/components/user-avatar"
import { useStore, useUser, useUserVip } from "@/stores/appStore"
import { Separator } from "./ui/separator"
import { ChevronRightIcon, LogoutIcon, VipLogoDrakenIcon } from "./fun-ui/icons";
import * as Icons from "./fun-ui/icons";
import { UserAccountNavItem } from "@/types";
import toast from "react-hot-toast";
import { useLocalClear } from "@/stores/localStore";
import { formatTime } from "@/lib/utils";
import { isPaidVip } from "@/lib/payment";
import { MemberLabel } from "./fun-ui/MemberLabel";
import { UpgradeVipButton } from "./payment/UpgradeVipButton";
import { MemberPlanText } from "./payment/MemberPlanText";
// import { UsageQuotasForSubNav } from "./dashboard/UsageQuotas";

export function UserAccountNav({
  className = '',
  iconOnly = false,
  navItems = [],
}: {
  className?: string;
  iconOnly?: boolean;
  navItems?: UserAccountNavItem[];
}) {
  const { user } = useUser();
  const logout = useStore((state) => state.logout);
  const { clear } = useLocalClear();
  const {
    vip,
    vipType,
    vipExtra,
    remainingDays,
  } = useUserVip();

  if (!user) {
    return (
      <div className="h-8 w-8 rounded-full">
        <svg width="100%" height="100%" viewBox="0 0 240 240"><g><path d="M120,0C53.9999,0,0,54,0,120C0,186,53.9999,240,120,240C186,240,240,186,240,120C240,54,186,0,120,0ZM192.6,171C174.6,191.4,148.8,204,120,204C91.1999,204,65.3999,191.4,47.3999,171C45.6,169.2,45.6,166.2,46.7999,163.8C59.9999,144.6,79.1999,132.6,100.2,127.8C83.9999,120.6,71.9999,103.8,71.9999,84C71.9999,55.8,96.5999,33,125.4,36.6C147,39,164.4,56.4,167.4,77.4001C170.4,100.2,157.8,120,139.2,128.4C160.2,132.6,179.4,144.6,192.6,164.4C194.4,166.2,193.8,169.2,192.6,171Z" fill="#EAEAEA" fillOpacity="1" /></g></svg>
      </div>
    );
  }
  return (
    <Popover>
      <PopoverTrigger className={className}>
        <UserAvatar size="sm" />
        {
          iconOnly ? null : <span>{user.nickname}</span>
        }
      </PopoverTrigger>
      <PopoverContent side="right" align="end" sideOffset={12} className="bg-background text-foreground shadow-[0px_3.2px_8px_0px_rgba(0,0,0,0.08)] -ml-1 border w-[240px]">
        {
          iconOnly ? (
            <>
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1 leading-none">
                  <p className="font-bold">{user.nickname}</p>
                </div>
              </div>
              <Separator className="my-1" />
            </>
          ) : null
        }
        {
          (isPaidVip(vip) && vipExtra?.expireTime) ? (
            <>
              <Link className="flex items-top px-4 py-2.5 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-2 cursor-pointer" href="/settings">

                <div className="flex flex-col">
                  <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-2">
                      <VipLogoDrakenIcon />
                      <MemberLabel><MemberPlanText vip={vip} vipType={vipType} /></MemberLabel>
                    </div>
                    <div className="flex flex-row items-center">
                      <MemberLabel>权益</MemberLabel><ChevronRightIcon size={14} />
                    </div>
                  </div>
                  <div className="text-annotation-foreground mt-1 whitespace-nowrap">会员有效期至：{formatTime(vipExtra.expireTime, 'YYYY-MM-DD')}</div>
                  <div className="text-annotation-foreground mt-0.5 whitespace-nowrap">
                    {
                      vipExtra?.rightsUpdateTime ? (
                        `权益刷新时间：${formatTime(vipExtra?.rightsUpdateTime, 'YYYY-MM-DD')}`
                      ) : (
                        `权益到期时间：${formatTime(vipExtra.expireTime, 'YYYY-MM-DD')}`
                      )
                    }
                  </div>
                  {
                    typeof remainingDays === 'number' ? (
                      <div className="mt-1 whitespace-nowrap">
                        {
                          remainingDays > 0 && remainingDays <= 15 && (
                            <MemberLabel className="mr-2">会员仅剩 {remainingDays} 天</MemberLabel>
                          )
                        }
                        {
                          remainingDays === 0 && (
                            <MemberLabel className="mr-2">会员今日到期</MemberLabel>
                          )
                        }
                        <UpgradeVipButton size="sm" withoutIcon={true} className="h-7 w-20 inline-flex">立即续费</UpgradeVipButton>
                      </div>
                    ) : null
                  }
                </div>
              </Link>
              <Separator className="my-1" />
            </>
          ) : null
        }
        {navItems.map((item, index) => {
          const Icon = Icons[item.icon];
          const isExternalLink = item.href?.startsWith('http://') || item.href?.startsWith('https://');
          const Component = isExternalLink ? 'a' : Link;
          const linkProps = item.target ? { target: item.target, rel: 'noopener noreferrer' } : {};
          return (
            <Component
              key={index}
              href={item.href}
              className="flex items-center px-4 py-2.5 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-2 cursor-pointer"
              {...linkProps}
            >
              {Icon && <Icon />}
              <span>{item.title}</span>
            </Component>
          );
        })}
        <Separator className="my-1" />
        <div
          className="flex items-center px-4 py-2.5 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-2 cursor-pointer"
          onClick={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            toast.loading('正在退出登录...');
            await logout();
            clear();
            window.location.href = '/login';
          }}
        >
          <LogoutIcon />
          <span>退出登录</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
